import {
  Box,
  Heading,
  HStack,
  IconButton,
  Separator,
  Text,
  Timeline,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { FaGlobe, FaKey, FaLink, FaListUl, FaRocket, FaShieldAlt } from 'react-icons/fa';
import type { LearnMoreSection } from '../LearnMore';
import useLearnMoreSections from '../hooks/useLearnMoreSections';

const SECTIONS: LearnMoreSection[] = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'key-features', label: 'Key Features' },
  { id: 'technical-details', label: 'Technical Details' },
  { id: 'summary', label: 'Summary' },
];

const TIMELINE_ICON_SIZE = 12;

const DESIGN_SYSTEM_BULLETS = [
  {
    title: 'Custom Website Templates',
    description:
      'Choose from professionally designed, mobile-friendly layouts created for clubs. Add your colours, logo, imagery, and content to match your club’s identity.',
  },
  {
    title: 'Automatic Membership Integration',
    description:
      'New members join and existing members renew directly on your website. Payments and details update instantly. No third-party portals, no manual tracking, and no chasing payments.',
  },
  {
    title: 'Club Blog / News',
    description:
      'Publish updates and announcements directly from your admin area of your own website. Posts appear instantly on your website.',
  },
  {
    title: 'Promo Codes & Special Offers',
    description:
      'Create promo codes for membership and session payments. Perfect for students, members who will only be joining for a limited time, or for seasonal offers.',
  },
  {
    title: 'Document Hosting',
    description:
      'Store and display all important club documents, including codes of conduct, AGM minutes, constitutions, policies, safety documents, and training guides.',
  },
  {
    title: 'Built-In Contact Form (With Bot Protection)',
    description:
      'Includes a secure Contact Us form with built-in spam and bot protection. Messages are emailed to your chosen addresses, and you can set rules to route specific types of enquiries to specific people — for example, sending welfare-related messages directly to your Welfare Officer.',
  },
  {
    title: 'Mailchimp & Instagram Integration',
    description: `Sync members to Mailchimp for newsletters and display your latest Instagram posts directly on your club's website homepage.`,
  },
  {
    title: 'Create sessions',
    description:
      'Members which you assign to have coach permissions can create sessions and events which then appear on the session booking app. The sessions also automatically populate the club sessions page so prospective members can see what is on offer.',
  },
  {
    title: 'Club championships',
    description: `Create a list of races or events which your club is promoting to it's members. These will then appear on the session booking app and the club championships website page. Members can automaticlly upload results and a live leaderboard will be generated.`,
  },
  {
    title: 'Enable partner clubs',
    description: `Within the app you can enable partner clubs to share selected sessions and events with your club.`,
  },
  {
    title: 'Account linking',
    description: `For those members who have children account linking is a useful tool by which a parent can link their child's membership account to their own. This allows parents to book sessions on behalf of their children within the app under thier own login details.`,
  },
  {
    title: 'Free session entitlements ',
    description: `Some clubs reward key volenteers with free sessions. This can be enabled and an admin user can select the members who recieve this entitlement.`,
  },
];

const SETUP_STEPS = [
  {
    icon: <FaGlobe size={TIMELINE_ICON_SIZE} />,
    title: '1. Choose & Register Your Domain',
    body: `Pick and register your domain with any provider you prefer (GoDaddy, 123 Reg, Cloudflare, etc.). If you alredy have a club website - this step will already be completed.`,
  },
  {
    icon: <FaListUl size={TIMELINE_ICON_SIZE} />,
    title: '2. Receive Your DNS Records',
    body: `We provide you with a set of Nameserver (NS) records. These are what tell the internet which system is responsible for your domain and where your website should be loaded from.

You can think of NS records as the “home address” for your domain — they direct visitors to the correct place so everything works.

To set things up, you simply copy these NS records into your domain provider’s control panel (e.g., GoDaddy, 123-Reg, Cloudflare). Once you add them, your domain will start using the Olympiq platform to manage all of its DNS settings.`,
  },
  {
    icon: <FaKey size={TIMELINE_ICON_SIZE} />,
    title: '3. Share existing DNS records',
    body: `Before you switch the NS records, any existing DNS records you rely on — such as:

- email records (MX or DKIM)
- verification TXT records
- subdomains

should be passed to Olympiq so we can recreate them on the new nameservers. This ensures nothing stops working when the domain moves over.`,
  },
  {
    icon: <FaRocket size={TIMELINE_ICON_SIZE} />,
    title: '4. Test the new site',
    body: `We'll initially deploy the new website to testing.your-club-website.org so you can test the new site and make sure everything is working as expected. In this time your old site is stil live, and working as before. Once you are happy and give the go-ahead we'll make the new site live on you-club-website.org.`,
  },
  {
    icon: <FaShieldAlt size={TIMELINE_ICON_SIZE} />,
    title: '5. Need to update your DNS records?',
    body: `You keep full domain ownership. If you ever need DNS updates—such as DKIM email records—just let us know and we’ll update them whenever you need.`,
  },
];

const CustomisedClubWebsite = (): React.JSX.Element => {
  useLearnMoreSections(SECTIONS, { pageTitle: 'Customised Club Website' });

  return (
    <VStack align="stretch" gap={12}>
      <Box>
        <Heading size="3xl" letterSpacing="-0.04em">
          Customised club website
        </Heading>
        <Text marginTop={4} color="gray.600" fontSize="lg" maxW="3xl" _dark={{ color: 'gray.300' }}>
          Want a new website - but don&apos;t have a club webmaster to hand? We can build you a
          customisable, fully managed club website within days! Olympiq can save you hours on admin,
          payment chasing, and answering confused members questions.
        </Text>
      </Box>

      <Separator />

      <SectionBlock id="introduction" title="Introduction">
        <Text>
          When you join Olympiq, your club receives a fully customisable website designed to work
          seamlessly with your membership system. Members sign up, renew, create sessions, and much
          more on your website - not a third party. One system, one login.
        </Text>
        <Text>
          All you need to do is bring the domain{' '}
          <Text as="span" fontStyle="italic">
            (my-club.org)
          </Text>{' '}
          and Olympiq will handle the rest. There&apos;s no technical work for you to do - and no
          need to find a club webmaster.
        </Text>
      </SectionBlock>

      <SectionBlock id="key-features" title="Key Features">
        <Text marginBottom={4}>
          We’re constantly adding new features and improving the platform. If there’s something
          you’d like that isn’t listed here, just let us know — we’re always open to feature
          requests. Below is a non-exhaustive list of key features your club website will have:
        </Text>
        <VStack as="ul" gap={4} align="stretch" margin={0} padding={0} listStyleType="none">
          {DESIGN_SYSTEM_BULLETS.map((bullet) => (
            <HStack as="li" key={bullet.title} align="start" gap={4}>
              <Box
                as="span"
                fontWeight="bold"
                fontSize="sm"
                minW={{ base: '180px', md: '220px' }}
                maxW={{ base: '180px', md: '220px' }}
                flexShrink={0}
              >
                {bullet.title}
              </Box>
              <Text color="gray.700" _dark={{ color: 'gray.300' }}>
                {bullet.description}
              </Text>
            </HStack>
          ))}
        </VStack>
      </SectionBlock>

      <SectionBlock id="technical-details" title="Technical Details">
        Even though your club website is fully hosted and managed, you still keep complete ownership
        of your domain. Here’s how the setup works:
        <Timeline.Root colorPalette="blue" size="lg" marginTop={6}>
          {SETUP_STEPS.map((step) => (
            <Timeline.Item key={step.title}>
              <Timeline.Connector>
                <Timeline.Separator />
                <Timeline.Indicator bg="gray.800" color="white">
                  {step.icon}
                </Timeline.Indicator>
              </Timeline.Connector>
              <Timeline.Content>
                <Timeline.Title fontSize="md" fontWeight="semibold">
                  {step.title}
                </Timeline.Title>
                <Timeline.Description>
                  <Text color="gray.600" whiteSpace="pre-line" _dark={{ color: 'gray.300' }}>
                    {step.body}
                  </Text>
                </Timeline.Description>
              </Timeline.Content>
            </Timeline.Item>
          ))}
        </Timeline.Root>
      </SectionBlock>

      <SectionBlock id="summary" title="Summary">
        <Text>
          Members can join and renew directly on your website, making the whole process clearer and
          far less confusing for them. Everything is in one place, which means fewer questions,
          fewer mistakes, and far fewer support queries for your club. Payments and membership
          records update automatically in the background, removing manual reconciliation and cutting
          admin time dramatically.
        </Text>
        <Text>
          You also never need a webmaster or tech volunteer. There’s no hosting or software to
          manage, and admins can update content easily through simple tools — the website keeps
          itself consistent and up to date.
        </Text>
        <Text>
          And if your club needs something specific, we’re always open to feature requests so the
          platform continues to evolve around your needs.
        </Text>
      </SectionBlock>
    </VStack>
  );
};

interface SectionBlockProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

const SectionBlock = ({ id, title, children }: SectionBlockProps): React.JSX.Element => (
  <Box as="section" id={id} scrollMarginTop="120px">
    <Heading size="2xl" fontWeight="bold" marginBottom={3}>
      {title}
      <IconButton
        aria-label="Copy link"
        variant="ghost"
        size="xs"
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
        }}
      >
        <FaLink />
      </IconButton>
    </Heading>
    <VStack align="stretch" gap={3}>
      {typeof children === 'string' ? <Text color="gray.600">{children}</Text> : children}
    </VStack>
  </Box>
);

export default CustomisedClubWebsite;
